import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/context/I18nContext";

function CountUp({ to, suffix = "", duration = 1.6, locale = "en" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / (duration * 1000), 1);
      setVal(Math.round(t * to));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  const formatted = locale === "ar"
    ? new Intl.NumberFormat("ar-SA").format(val)
    : val.toLocaleString("en-US");

  return (
    <span ref={ref} className="tabular-nums">
      {formatted}{suffix}
    </span>
  );
}

const STAT_VALUES = [
  { value: 1000, suffix: "+" },
  { value: 18, suffix: "" },
  { value: 240, suffix: "+" },
  { value: 4, suffix: "" },
];

export default function About() {
  const { t, lang } = useI18n();

  return (
    <section id="about" className="bg-secondary text-secondary-foreground grain" data-testid="about-section">
      <div className="container-page section-y">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="overline">{t.about.eyebrow}</span>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {t.about.heading1} <span className="text-primary">{t.about.heading1Accent}</span>
              {t.about.heading2}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 space-y-6 text-secondary-foreground/75 text-base sm:text-lg leading-relaxed"
          >
            <p>
              <span className="text-secondary-foreground font-semibold">{t.about.paragraph1Strong}</span>
              {t.about.paragraph1}
            </p>
            <p>{t.about.paragraph2}</p>
          </motion.div>
        </div>

        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="about-stats-grid">
          {t.about.stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-secondary p-8 lg:p-10"
              data-testid={`about-stat-${i}`}
            >
              <div className="text-5xl lg:text-6xl font-black text-white">
                <CountUp to={STAT_VALUES[i].value} suffix={STAT_VALUES[i].suffix} locale={lang} />
              </div>
              <div className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-primary">
                {s.label}
              </div>
              <div className="mt-2 text-sm text-white/55">{s.description}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
